import "./GanttTaskGroup.css";

import { useEffect, useRef, useState } from "react";
import { JSX } from "react/jsx-runtime";

import { CircleFilledIcon, DiamondOutlineIcon } from "../../../../../assets";
import { convertDateToUtcStartOfDay, isPastDateByDay } from "../../../../../utils";
import { Button } from "../../../../forms/Button";
import { DEFAULT_TASK_LIMIT, GROUP_ROW_HEIGHT, TASK_ROW_HEIGHT } from "../../constants";
import { GanttTask, GanttTaskGroupProps } from "../../models";
import { resolveTaskStatus } from "../../utils";
import { GanttTaskRow } from "../GanttTaskRow";
import { GanttTodayLine } from "../GanttTodayLine";

/**
 * A task is overdue when its deadline's calendar day has passed and it isn't complete. Deadline and
 * "now" are both normalised to their UTC day start before comparing — the app's convention for
 * date-only values — so this fallback agrees exactly with the server's `overdueTaskCount` and with
 * the per-task `isOverdue` the Tasks dashboard maps.
 */
const isOverdue = (task: GanttTask): boolean => {
  const status = resolveTaskStatus(task.status);
  if (!task.deadlineDate || status === "Complete" || status === "Completed") return false;
  return isPastDateByDay(convertDateToUtcStartOfDay(task.deadlineDate), convertDateToUtcStartOfDay(new Date()));
};

const CircleIcon = ({ color }: { color?: string }): JSX.Element => (
  <CircleFilledIcon
    className="GanttGroupIcon"
    width="12"
    height="12"
    style={{ color: color ?? "var(--color-grey-500)" }}
    aria-hidden
  />
);

export const GanttTaskGroup = ({
  group,
  isExpanded,
  viewStart,
  pixelsPerDay,
  timelineWidth,
  todayX,
  labelWidth,
  initialTaskLimit = DEFAULT_TASK_LIMIT,
  onToggle,
  onTaskClick,
  onObjectClick,
  onShowMore,
  onShowTooltip,
  onHideTooltip,
}: GanttTaskGroupProps): JSX.Element => {
  const step = Math.max(initialTaskLimit, 1);
  const [visibleCount, setVisibleCount] = useState(step);

  // `group.tasks` arrives already filtered (assignee/object/status/search are applied
  // centrally in useGanttChart), so the rows, counts and empty state all describe the
  // same visible task set.
  const visibleTasks = group.tasks.slice(0, visibleCount);
  /** Loaded but not yet revealed — what the local expander has in hand, and all it can reveal. */
  const hiddenLoaded = group.tasks.length - visibleTasks.length;
  const nextBatch = Math.min(step, hiddenLoaded);

  /*
    Counts prefer the server's totals (`taskCount` / `milestoneTaskCount` / `overdueTaskCount`) so a
    group whose rows are paged or lazily loaded still reports its real size; they fall back to
    counting the loaded rows when a caller supplies none. `useGanttChart` strips the server totals
    whenever its own filters are active, because a total for the unfiltered set would then contradict
    the rendered rows.
  */
  const totalCount = group.taskCount ?? group.tasks.length;
  const overdueCount = group.overdueTaskCount ?? group.tasks.filter(isOverdue).length;
  const activeCount = Math.max(totalCount - overdueCount, 0);
  const milestoneCount = group.milestoneTaskCount ?? group.tasks.filter((t) => t.isMilestone).length;

  // The server holds further rows once the loaded ones are all on screen.
  const canLoadMore = Boolean(group.hasMoreTasks) && hiddenLoaded === 0;

  /*
    "Remaining" means one thing on both buttons: rows of this group not on screen yet. It deliberately
    spans both places they can be — loaded but collapsed behind the expander, and still on the server —
    because the distinction is an implementation detail of how they arrive, and a user reading "10
    remaining" over a group of 30 is simply being told the wrong number. It was previously the loaded
    page for the expander and the unfetched rows for the fetch button, so the count jumped as the
    buttons swapped.

    Measured against `pagedTaskCount` — the total belonging to the request that pages these rows — and
    never against `taskCount`, which answers the group-header request. Those are different queries,
    scoped differently (statuses, task type), so the header's total drifted from the pages arriving.
    With no pager total (never expanded, or the chart's own filters stripped it) only the loaded rows
    can be spoken for, and the fetch button drops its count rather than printing a figure it can't
    stand behind.
  */
  const pagedTotal = group.pagedTaskCount;
  const remaining = Math.max((pagedTotal ?? group.tasks.length) - visibleTasks.length, 0);
  const serverShowMoreText = pagedTotal === undefined ? "Show more" : `Show more (${remaining} remaining)`;

  /*
    Reveal a page the moment it lands. `visibleCount` gates the loaded rows, so a fetched page of 20
    over a step of 10 otherwise stayed hidden behind two more clicks — and the button flipped from
    "Show more (20 remaining)" to "Show 10 more (20 remaining)", the same number now counting
    something else entirely. Only fetched rows do this: the local expander stays in `step` increments.
  */
  const loadedCountRef = useRef(group.tasks.length);
  const isAwaitingPageRef = useRef(false);

  useEffect(() => {
    const loaded = group.tasks.length;
    const grew = loaded > loadedCountRef.current;
    loadedCountRef.current = loaded;

    if (!grew || !isAwaitingPageRef.current) return;

    isAwaitingPageRef.current = false;
    setVisibleCount(loaded);
  }, [group.tasks.length]);

  const loadMore = (): void => {
    isAwaitingPageRef.current = true;
    onShowMore?.(group.id);
  };

  return (
    <div className="GanttTaskGroup">
      {/* ── Group header row ──────────────────────────────────────────────── */}
      <div className="GanttGroupRow" style={{ height: GROUP_ROW_HEIGHT }}>
        <div className="GanttGroupRowLabel" style={{ width: labelWidth }}>
          <button
            type="button"
            className={`GanttGroupToggle${isExpanded ? " GanttGroupToggleExpanded" : ""}`}
            onClick={() => onToggle(group.id)}
            aria-expanded={isExpanded}
            aria-controls={`gantt-group-${group.id}`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <CircleIcon color={group.iconColor} />
          {onObjectClick ? (
            <button
              type="button"
              className="GanttGroupName GanttGroupNameButton body2"
              title={group.name}
              onClick={() => onObjectClick(group.id)}
            >
              {group.name}
            </button>
          ) : (
            <span className="GanttGroupName body2" title={group.name}>
              {group.name}
            </span>
          )}
          {group.objectType && (
            <span
              className={`GanttGroupType body4 GanttGroupType_${group.objectType.toLowerCase()}`}
              title={group.objectType}
            >
              {group.objectType}
            </span>
          )}
          {totalCount > 0 && (
            <span className="GanttGroupCount body4" title={`${activeCount} tasks`}>
              {activeCount}
            </span>
          )}
          {overdueCount > 0 && (
            <span className="GanttGroupOverdueCount body4" title={`${overdueCount} overdue`}>
              {overdueCount} overdue
            </span>
          )}
          {milestoneCount > 0 && (
            <span className="GanttGroupMilestoneCount body4" title={`${milestoneCount} milestones`}>
              {/* 10px, not 8: the diamond is a rotated square, so it fills only ~82% of its box. */}
              <DiamondOutlineIcon width="10" height="10" aria-hidden />
              {milestoneCount}
            </span>
          )}
        </div>

        {/* Group timeline cell */}
        <div className="GanttGroupRowTimeline" style={{ width: timelineWidth }}>
          <GanttTodayLine x={todayX} />
        </div>
      </div>

      {/* ── Task rows ────────────────────────────────────────────────────── */}
      {isExpanded && (
        <div id={`gantt-group-${group.id}`} role="rowgroup">
          {visibleTasks.map((task) => (
            <GanttTaskRow
              key={task.id}
              task={task}
              groupId={group.id}
              viewStart={viewStart}
              pixelsPerDay={pixelsPerDay}
              timelineWidth={timelineWidth}
              todayX={todayX}
              labelWidth={labelWidth}
              onClick={onTaskClick}
              onShowTooltip={onShowTooltip}
              onHideTooltip={onHideTooltip}
            />
          ))}

          {(group.tasks.length > step || canLoadMore) && (
            <div className="GanttGroupShowMore">
              {hiddenLoaded > 0 && (
                <Button
                  text={`Show ${nextBatch} more (${remaining} remaining)`}
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  onClick={() => setVisibleCount((c) => c + step)}
                />
              )}
              {/* Nothing left locally, but the server has more — ask the caller to fetch a page. */}
              {canLoadMore && (
                <Button
                  text={group.isLoadingTasks ? "Loading…" : serverShowMoreText}
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  disabled={group.isLoadingTasks}
                  onClick={loadMore}
                />
              )}
              {hiddenLoaded === 0 && !canLoadMore && (
                <Button
                  text="Show less"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  onClick={() => setVisibleCount(step)}
                />
              )}
            </div>
          )}

          {group.tasks.length === 0 && (
            <div className="GanttGroupEmpty" style={{ height: TASK_ROW_HEIGHT }}>
              <div className="GanttGroupEmptyLabel body3" style={{ width: labelWidth }}>
                {group.isLoadingTasks ? "Loading tasks…" : "No matching tasks"}
              </div>
              {/* Classed so it grows with the other timeline cells and the row reaches the body edge. */}
              <div className="GanttGroupEmptyTimeline" style={{ width: timelineWidth }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
